package com.ijoin.ui.screens

import androidx.compose.foundation.ScrollState
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material.icons.outlined.CalendarToday
import androidx.compose.material.icons.outlined.LocationOn
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Divider
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.material3.TopAppBarDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import com.ijoin.data.model.Ticket
import com.ijoin.data.model.TicketStatus
import com.ijoin.i18n.Strings
import com.ijoin.i18n.strings
import com.ijoin.getPlatform
import com.ijoin.platform.createTimeZone
import com.ijoin.platform.isWebMobile
import com.ijoin.ui.components.MainBottomNavigationBar
import com.ijoin.ui.components.SideNavigationRail
import com.ijoin.ui.components.TicketQrCode
import com.ijoin.ui.tickets.TicketViewModel
import com.ijoin.ui.test.TestTags
import kotlinx.datetime.Instant
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import com.ijoin.ui.components.RefreshableContainer
import com.ijoin.ui.components.rememberPullToRefreshState
import com.ijoin.util.datetime.monthNumberCompat
import com.ijoin.ui.design.EventCardAccentDark
import com.ijoin.ui.design.EventCardAccentSoft
import com.ijoin.ui.design.EventCardAlert
import com.ijoin.ui.design.EventCardBackground
import com.ijoin.ui.design.EventCardOutline
import com.ijoin.ui.design.EventCardSurface
import com.ijoin.ui.design.EventCardWarm
import com.ijoin.ui.design.EventCardWarmSoft
import com.ijoin.ui.design.EventDangerButton
import com.ijoin.ui.design.EventOutlineButton
import com.ijoin.ui.design.EventPill
import com.ijoin.ui.design.EventSectionCard
import com.ijoin.ui.design.EventTextAction
import com.ijoin.ui.design.eventBackgroundBrush

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun TicketScreen(
    ticketId: String,
    onBackClick: () -> Unit,
    onEventClick: (String) -> Unit,
    onOpenHome: () -> Unit,
    onOpenExplore: () -> Unit,
    onCreateEventClick: () -> Unit,
    onOpenTickets: () -> Unit,
    onOpenProfile: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val strings = strings()
    val viewModel = remember(ticketId) { TicketViewModel(ticketId) }
    val uiState by viewModel.uiState.collectAsState()
    val snackbarHostState = remember { SnackbarHostState() }
    var showCancelDialog by remember { mutableStateOf(false) }
    val scrollState = rememberScrollState()

    LaunchedEffect(uiState.cancellationSuccess) {
        if (uiState.cancellationSuccess) {
            snackbarHostState.showSnackbar(strings.ticketCancellationSuccess)
            viewModel.consumeMessages()
        }
    }

    LaunchedEffect(uiState.cancelError) {
        uiState.cancelError?.let {
            val message = it.ifBlank { strings.ticketCancellationFailure }
            snackbarHostState.showSnackbar(message)
            viewModel.consumeMessages()
        }
    }

    val currentTicket = uiState.ticket
    val platform = remember { getPlatform() }
    val isDesktopWeb = platform.isWeb && !isWebMobile()
    val onTabSelected: (String) -> Unit = { tab ->
        when (tab) {
            "home" -> onOpenHome()
            "explore" -> onOpenExplore()
            "create" -> onCreateEventClick()
            "tickets" -> onOpenTickets()
            "profile" -> onOpenProfile()
        }
    }
    val scaffoldModifier = modifier
        .fillMaxSize()
        .testTag(TestTags.TicketDetailsScreen.ROOT)

    val mainScaffold: @Composable () -> Unit = {
        Scaffold(
            modifier = scaffoldModifier,
            containerColor = EventCardBackground,
            topBar = {
                TopAppBar(
                    title = {
                        Text(
                            text = strings.ticketDetailsTitle,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis
                        )
                    },
                    navigationIcon = {
                        IconButton(onClick = onBackClick) {
                            Icon(Icons.Default.ArrowBack, contentDescription = strings.back)
                        }
                    },
                    colors = TopAppBarDefaults.topAppBarColors(
                        containerColor = EventCardBackground,
                        titleContentColor = EventCardAccentDark,
                        navigationIconContentColor = EventCardAccentDark
                    )
                )
            },
            snackbarHost = { SnackbarHost(snackbarHostState) },
            bottomBar = {
                if (!isDesktopWeb) {
                    MainBottomNavigationBar(
                        selectedTab = "tickets",
                        onTabSelected = onTabSelected
                    )
                }
            }
        ) { paddingValues ->
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .background(eventBackgroundBrush())
                    .padding(paddingValues)
            ) {
                when {
                    uiState.isLoading -> {
                        Box(
                            modifier = Modifier.fillMaxSize(),
                            contentAlignment = Alignment.Center
                        ) {
                            androidx.compose.material3.CircularProgressIndicator()
                        }
                    }

                    uiState.error != null -> {
                        val message = uiState.error?.ifBlank { null } ?: strings.ticketLoadError
                        ErrorState(
                            message = message,
                            onRetry = viewModel::loadTicket,
                            modifier = Modifier.fillMaxSize()
                        )
                    }

                    currentTicket != null -> {
                        val pullState = rememberPullToRefreshState(
                            refreshing = uiState.isLoading,
                            onRefresh = viewModel::loadTicket,
                            canRefresh = { scrollState.value == 0 }
                        )
                        RefreshableContainer(
                            modifier = Modifier.fillMaxSize(),
                            state = pullState,
                            enabled = true
                        ) { pullModifier ->
                            TicketDetailsContent(
                                ticket = currentTicket,
                                isCancelling = uiState.isCancelling,
                                onCancelClick = { showCancelDialog = true },
                                onEventClick = onEventClick,
                                modifier = pullModifier,
                                scrollState = scrollState
                            )
                        }
                    }
                }
            }
        }
    }

    if (isDesktopWeb) {
        Row(modifier = Modifier.fillMaxSize()) {
            SideNavigationRail(
                selectedTab = "tickets",
                onTabSelected = onTabSelected,
                showAppStoreLinks = true
            )
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .background(eventBackgroundBrush()),
                contentAlignment = Alignment.TopCenter
            ) {
                Box(modifier = Modifier.fillMaxWidth(0.6f)) {
                    mainScaffold()
                }
            }
        }
    } else {
        mainScaffold()
    }

    if (showCancelDialog) {
        AlertDialog(
            onDismissRequest = {
                if (!uiState.isCancelling) {
                    showCancelDialog = false
                }
            },
            title = { Text(strings.ticketCancellationConfirmTitle) },
            text = { Text(strings.ticketCancellationConfirmMessage) },
            confirmButton = {
                EventTextAction(
                    text = strings.cancelTicketAction,
                    onClick = {
                        showCancelDialog = false
                        viewModel.cancelTicket()
                    },
                    enabled = !uiState.isCancelling,
                    modifier = Modifier.testTag(TestTags.TicketDetailsScreen.CONFIRM_CANCEL_BUTTON),
                    contentColor = EventCardAlert,
                    isLoading = uiState.isCancelling
                )
            },
            dismissButton = {
                EventTextAction(
                    text = strings.cancel,
                    onClick = { showCancelDialog = false }
                )
            }
        )
    }
}

@Composable
private fun TicketDetailsContent(
    ticket: Ticket,
    isCancelling: Boolean,
    onCancelClick: () -> Unit,
    onEventClick: (String) -> Unit,
    modifier: Modifier = Modifier,
    scrollState: ScrollState
) {
    val strings = strings()

    Column(
        modifier = modifier
            .fillMaxSize()
            .verticalScroll(scrollState)
            .padding(16.dp),
        verticalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        Text(
            text = ticket.eventTitle ?: strings.ticketDetailsTitle,
            style = MaterialTheme.typography.headlineSmall,
            color = EventCardAccentDark,
            modifier = Modifier.clickable {
                if (ticket.eventId.isNotBlank()) {
                    onEventClick(ticket.eventId)
                }
            }
        )

        StatusBadge(ticket.status)

        TicketInfoCard(ticket)

        BoxWithConstraints(
            modifier = Modifier.fillMaxWidth(),
            contentAlignment = Alignment.Center
        ) {
            val targetSize = if (maxWidth <= 320.dp) maxWidth * 0.9f else 320.dp
            TicketQrCode(
                data = ticket.qrCode,
                modifier = Modifier
                    .size(targetSize)
            )
        }

        if (ticket.status == TicketStatus.ACTIVE) {
            EventDangerButton(
                text = strings.cancelTicketAction,
                onClick = onCancelClick,
                enabled = !isCancelling,
                isLoading = isCancelling,
                modifier = Modifier
                    .fillMaxWidth()
                    .testTag(TestTags.TicketDetailsScreen.CANCEL_BUTTON)
            )
        } else if (ticket.status == TicketStatus.REFUNDED) {
            Text(
                text = strings.ticketCancellationSuccess,
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }
    }
}

@Composable
private fun TicketInfoCard(ticket: Ticket) {
    val strings = strings()
    val dateTime = strings.formatTicketDate(ticket.occurrenceDateTime ?: ticket.eventDate, ticket.eventTimeZone)

    EventSectionCard {
        InfoRow(
            icon = Icons.Outlined.CalendarToday,
            label = strings.dateLabel,
            value = dateTime ?: strings.notSelected
        )
        InfoRow(
            icon = Icons.Outlined.LocationOn,
            label = strings.location,
            value = ticket.eventLocation ?: strings.notSelected
        )

        Divider(color = EventCardOutline)

        Text(strings.ticketNumberLabel, style = MaterialTheme.typography.labelMedium)
        Text(
            text = ticket.ticketNumber,
            style = MaterialTheme.typography.titleMedium,
            color = EventCardAccentDark
        )

        ticket.priceTierName?.let { tier ->
            Divider(color = EventCardOutline)
            Text(strings.ticketPrice, style = MaterialTheme.typography.labelMedium)
            Text(tier, style = MaterialTheme.typography.bodyLarge)
        }

        ticket.paidPrice?.let { amount ->
            Divider(color = EventCardOutline)
            Text(strings.costLabel, style = MaterialTheme.typography.labelMedium)
            Text(amount.toString(), style = MaterialTheme.typography.bodyLarge)
        }

        (ticket.holderName ?: ticket.holderEmail)?.let { holder ->
            Divider(color = EventCardOutline)
            Text(strings.ticketHolderLabel, style = MaterialTheme.typography.labelMedium)
            Text(holder, style = MaterialTheme.typography.bodyLarge)
        }
    }
}

@Composable
private fun InfoRow(
    icon: ImageVector,
    label: String,
    value: String
) {
    Row(
        horizontalArrangement = Arrangement.spacedBy(12.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Surface(
            color = EventCardAccentSoft,
            shape = CircleShape
        ) {
            Icon(
                imageVector = icon,
                contentDescription = null,
                tint = EventCardAccentDark,
                modifier = Modifier.padding(8.dp)
            )
        }
        Column {
            Text(label, style = MaterialTheme.typography.labelMedium, color = MaterialTheme.colorScheme.onSurfaceVariant)
            Text(value, style = MaterialTheme.typography.bodyLarge)
        }
    }
}

@Composable
private fun StatusBadge(status: TicketStatus) {
    val strings = strings()
    val (text, backgroundColor, textColor) = when (status) {
        TicketStatus.ACTIVE -> Triple(strings.ticketStatusActive, EventCardAccentSoft, EventCardAccentDark)
        TicketStatus.CANCELLED -> Triple(
            strings.ticketStatusCancelled,
            EventCardOutline,
            MaterialTheme.colorScheme.onSurfaceVariant
        )
        TicketStatus.REFUNDED -> Triple(strings.ticketStatusRefunded, EventCardWarmSoft, EventCardWarm)
        TicketStatus.USED -> Triple(strings.ticketStatusUsed, EventCardSurface, EventCardAccentDark)
    }
    EventPill(
        text = text,
        backgroundColor = backgroundColor,
        textColor = textColor,
        borderColor = EventCardOutline
    )
}

@Composable
private fun ErrorState(
    message: String,
    onRetry: () -> Unit,
    modifier: Modifier = Modifier
) {
    val strings = strings()
    Column(
        modifier = modifier.padding(32.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center
    ) {
        Text(
            text = message,
            style = MaterialTheme.typography.bodyLarge,
            textAlign = TextAlign.Center
        )
        Spacer(modifier = Modifier.height(16.dp))
        EventOutlineButton(
            text = strings.retry,
            onClick = onRetry
        )
    }
}

private fun Strings.formatTicketDate(instant: Instant?, timeZone: String? = null): String? {
    if (instant == null) return null
    val zone = if (!timeZone.isNullOrBlank()) createTimeZone(timeZone) else TimeZone.UTC
    val dateTime = instant.toLocalDateTime(zone)
    val date = dateTime.date
    val time = dateTime.time
    return buildString {
        append(date.dayOfMonth)
        append(' ')
        append(monthNameGenitive(date.monthNumberCompat()))
        append(' ')
        append(date.year)
        append(' ')
        append(time.hour.toString().padStart(2, '0'))
        append(':')
        append(time.minute.toString().padStart(2, '0'))
    }
}

private fun parseLocalDateTime(raw: String): LocalDateTime? {
    val direct = runCatching { LocalDateTime.parse(raw) }.getOrNull()
    if (direct != null) return direct
    val withoutZone = raw.removeSuffix("Z")
    val tIndex = withoutZone.indexOf('T')
    val offsetIndex = if (tIndex >= 0) {
        val plusIdx = withoutZone.indexOf('+', startIndex = tIndex + 1)
        val minusIdx = withoutZone.indexOf('-', startIndex = tIndex + 1)
        listOf(plusIdx, minusIdx).filter { it >= 0 }.minOrNull() ?: -1
    } else {
        -1
    }
    val trimmed = if (offsetIndex >= 0) {
        withoutZone.substring(0, offsetIndex)
    } else {
        withoutZone
    }
    return runCatching { LocalDateTime.parse(trimmed) }.getOrNull()
}
